import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Box,
  Grid,
  Typography,
  CircularProgress,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tooltip,
  IconButton,
  Card,
  CardContent,
} from "@mui/material";
import {
  Receipt,
  CheckCircleOutline,
  HourglassEmpty,
  FilterList,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import usePaidInvoice from "../../../hooks/usePaidInvoice";
import useUnpaidInvoice from "../../../hooks/useUnpaidInvoice";
import { useForm, FormProvider } from "react-hook-form";
import CustomDatePicker from "../../../components/CustomDatePicker";

function InvoiceStatTile({ label, value, color, bg, icon: Icon, loading }) {
  return (
    <Box
      sx={{
        p: 2,
        borderRadius: "12px",
        backgroundColor: (theme) => (theme.palette.mode === "dark" ? "background.paper" : bg),
        border: `1px solid ${color}20`,
        transition: "all 0.2s ease-in-out",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        "&:hover": {
          transform: "translateY(-1px)",
          boxShadow: `0 4px 12px ${color}15`,
          border: `1px solid ${color}40`,
        },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: "10px",
            backgroundColor: color,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            boxShadow: `0 4px 10px ${color}30`,
          }}
        >
          <Icon sx={{ fontSize: 20, color: "#fff" }} />
        </Box>
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography
              sx={{
                fontSize: "11px",
                fontWeight: 700,
                color: "text.secondary",
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                mb: 0.25,
              }}
            >
              {label}
            </Typography>
            {loading && <CircularProgress size={12} sx={{ color }} />}
          </Box>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 800,
              color: "text.primary",
              lineHeight: 1.1,
              opacity: loading ? 0.6 : 1,
              transition: "opacity 0.2s ease-in-out",
            }}
          >
            ₹{Number(value || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

const getInitialDates = () => {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return [start, end];
};

const InvoiceStatsWidget = () => {
  const { getAllPaidInvoice } = usePaidInvoice();
  const { getAllUnpaidInvoice } = useUnpaidInvoice();

  const [paidTotal, setPaidTotal] = useState(0);
  const [unpaidTotal, setUnpaidTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const datePickerRef = useRef(null);

  const methods = useForm({
    defaultValues: {
      invoiceDate: getInitialDates(),
      invoiceDateStr: "",
    },
  });

  const { control, watch } = methods;
  const invoiceDate = watch("invoiceDate");

  const formatRange = useCallback((dates) => {
    if (!dates || dates.length === 0) return "";

    const pad = (n) => String(n).padStart(2, "0");
    const formatSingle = (d, time) => {
      if (!d) return "";
      if (typeof d.format === "function") {
        return `${d.format("YYYY-MM-DD")} ${time}`;
      }
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${time}`;
    };

    if (dates.length === 2) {
      return `${formatSingle(dates[0], "00:00:00")},${formatSingle(dates[1], "23:59:59")}`;
    } else {
      return `${formatSingle(dates[0], "00:00:00")},${formatSingle(dates[0], "23:59:59")}`;
    }
  }, []);

  const formattedDateRange = formatRange(invoiceDate);

  const fetchStats = useCallback(
    async (dateRange) => {
      if (!dateRange) return;
      setLoading(true);
      try {
        const basePayload = (fieldName) => ({
          filters: [
            {
              fieldName,
              fieldValue: dateRange,
              op: "btn",
              dataType: "date",
              condition: "and",
              isJoin: false,
            },
          ],
          pagination: { pageNumber: 1, pageSize: 10000 },
          sortFields: [],
        });

        const [paidResult, unpaidResult] = await Promise.all([
          getAllPaidInvoice(basePayload("invoiceDate")),
          getAllUnpaidInvoice(basePayload("invoiceDate")),
        ]);

        const sumField = (arr, field) =>
          (arr || []).reduce((acc, row) => acc + parseFloat(row[field] || 0), 0);

        const paidRows = paidResult?.data || [];
        const unpaidRows = unpaidResult?.data || [];

        setPaidTotal(sumField(paidRows, "finalInvoiceAmount"));
        setUnpaidTotal(sumField(unpaidRows, "finalInvoiceAmount"));
      } catch (e) {
        console.error("InvoiceStatsWidget fetch error:", e);
      } finally {
        setLoading(false);
      }
    },
    [getAllPaidInvoice, getAllUnpaidInvoice],
  );

  useEffect(() => {
    fetchStats(formattedDateRange);
  }, [formattedDateRange]);

  useEffect(() => {
    if (invoiceDate && invoiceDate.length === 2 && datePickerRef.current) {
      datePickerRef.current.closeCalendar();
    }
  }, [invoiceDate]);

  const displayDateRange = () => {
    if (!invoiceDate || invoiceDate.length === 0) return "";
    const options = { month: "short", day: "numeric", year: "numeric" };
    const getJSDate = (d) => {
      if (!d) return null;
      if (d instanceof Date) return d;
      if (typeof d.toDate === "function") return d.toDate();
      return new Date(d);
    };
    const startDate = getJSDate(invoiceDate[0]);
    const endDate = invoiceDate[1] ? getJSDate(invoiceDate[1]) : startDate;
    if (!startDate) return "";
    return `${startDate.toLocaleDateString("default", options)} - ${endDate.toLocaleDateString("default", options)}`;
  };

  const totalValue = paidTotal + unpaidTotal;
  const paidPercentage = totalValue > 0 ? (paidTotal / totalValue) * 100 : 0;
  const unpaidPercentage = totalValue > 0 ? (unpaidTotal / totalValue) * 100 : 0;

  return (
    <Card
      sx={{
        width: "100%",
        borderRadius: "16px",
        boxShadow: (theme) =>
          theme.palette.mode === "dark"
            ? "0 4px 20px rgba(0, 0, 0, 0.4)"
            : "0 4px 20px rgba(0, 0, 0, 0.05)",
        border: (theme) =>
          theme.palette.mode === "dark"
            ? "1px solid rgba(255, 255, 255, 0.08)"
            : "1px solid rgba(0, 0, 0, 0.04)",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "background.paper",
        transition: "box-shadow 0.3s ease-in-out",
        "&:hover": {
          boxShadow: (theme) =>
            theme.palette.mode === "dark"
              ? "0 8px 30px rgba(0, 0, 0, 0.6)"
              : "0 8px 30px rgba(0, 0, 0, 0.08)",
        },
      }}
    >
      {/* Card Header */}
      <Box
        sx={{
          p: 3,
          pb: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: (theme) =>
            theme.palette.mode === "dark"
              ? "1px solid rgba(255, 255, 255, 0.08)"
              : "1px solid #f1f5f9",
        }}
      >
        <Box>
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 700, color: "text.secondary", textTransform: "capitalize" }}
          >
            Invoice Summary
          </Typography>
          <Typography sx={{ fontSize: "12px", color: "text.secondary", fontWeight: 500, mt: 0.25 }}>
            {displayDateRange()}
          </Typography>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {/* Custom Date Picker filter wrapper */}
          <FormProvider {...methods}>
            <Box sx={{ minWidth: 220 }}>
              <CustomDatePicker
                name="invoiceDate"
                nameTemp="invoiceDateStr"
                label="Select Date Range"
                range={true}
                control={control}
                size="small"
                ref={datePickerRef}
              />
            </Box>
          </FormProvider>

          {/* Refresh button */}
          <Tooltip title="Refresh">
            <IconButton
              size="small"
              onClick={() => fetchStats(formattedDateRange)}
              sx={{
                border: (theme) =>
                  theme.palette.mode === "dark"
                    ? "1px solid rgba(255, 255, 255, 0.15)"
                    : "1px solid #e2e8f0",
                borderRadius: "8px",
                color: (theme) =>
                  theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.7)" : "#64748b",
                bgcolor: (theme) =>
                  theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.05)" : "#f8fafc",
                transition: "all 0.2s",
                "&:hover": {
                  backgroundColor: (theme) =>
                    theme.palette.mode === "dark" ? "rgba(59, 130, 246, 0.15)" : "#eff6ff",
                  borderColor: "#3b82f6",
                  color: "#3b82f6",
                  transform: "rotate(180deg)",
                },
              }}
            >
              <RefreshIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Card Content */}
      <CardContent
        sx={{
          p: 3,
          pt: 2,
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          justifyContext: "center",
        }}
      >
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <InvoiceStatTile
              label="Total Invoice Value"
              value={totalValue}
              color="#8b5cf6"
              bg="#f5f3ff"
              icon={Receipt}
              loading={loading}
            />
          </Grid>
          <Grid item xs={12}>
            <InvoiceStatTile
              label="Paid Invoice Value"
              value={paidTotal}
              color="#22c55e"
              bg="#f0fdf4"
              icon={CheckCircleOutline}
              loading={loading}
            />
          </Grid>
          <Grid item xs={12}>
            <InvoiceStatTile
              label="Unpaid Invoice Value"
              value={unpaidTotal}
              color="#f97316"
              bg="#fff7ed"
              icon={HourglassEmpty}
              loading={loading}
            />
          </Grid>
        </Grid>

        {/* Ratio Progress Bar */}
        <Box
          sx={{
            mt: 3,
            pt: 2,
            borderTop: (theme) =>
              theme.palette.mode === "dark"
                ? "1px solid rgba(255, 255, 255, 0.08)"
                : "1px solid #f1f5f9",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1,
            }}
          >
            <Typography
              sx={{
                fontSize: "11px",
                fontWeight: 700,
                color: "text.secondary",
                textTransform: "uppercase",
                letterSpacing: "0.05em",
              }}
            >
              Collection Ratio (Paid / Unpaid)
            </Typography>
            <Typography sx={{ fontSize: "12px", fontWeight: 800, color: "#22c55e" }}>
              {paidPercentage.toFixed(1)}% Paid
            </Typography>
          </Box>

          <Box
            sx={{
              height: 10,
              borderRadius: "5px",
              bgcolor: (theme) =>
                theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.08)" : "#f1f5f9",
              overflow: "hidden",
              display: "flex",
              border: (theme) =>
                theme.palette.mode === "dark"
                  ? "1px solid rgba(255, 255, 255, 0.12)"
                  : "1px solid #e2e8f0",
            }}
          >
            {totalValue > 0 ? (
              <>
                <Box
                  sx={{
                    width: `${paidPercentage}%`,
                    bgcolor: "#22c55e",
                    backgroundImage:
                      "linear-gradient(45deg, rgba(255,255,255,.15) 25%, " +
                      "transparent 25%, transparent 50%, " +
                      "rgba(255,255,255,.15) 50%, rgba(255,255,255,.15) 75%, " +
                      "transparent 75%, transparent)",
                    backgroundSize: "1rem 1rem",
                    transition: "width 0.6s cubic-bezier(0.4, 0, 0.2, 1)",
                  }}
                />
                <Box
                  sx={{
                    width: `${unpaidPercentage}%`,
                    bgcolor: "#f97316",
                    transition: "width 0.6s cubic-bezier(0.4, 0, 0.2, 1)",
                  }}
                />
              </>
            ) : (
              <Box
                sx={{
                  width: "100%",
                  bgcolor: "#cbd5e1",
                  textAlign: "center",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              />
            )}
          </Box>

          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}>
            <Typography sx={{ fontSize: "10px", fontWeight: 600, color: "#22c55e" }}>
              ● Paid (₹{Number(paidTotal).toLocaleString("en-IN")})
            </Typography>
            <Typography sx={{ fontSize: "10px", fontWeight: 600, color: "#f97316" }}>
              ● Unpaid (₹{Number(unpaidTotal).toLocaleString("en-IN")})
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default InvoiceStatsWidget;
